import * as Sentry from '@sentry/react';
import { execGraphqlWithFetch } from '@utils/gql';
import { Usereventlogs } from '@gql/base-types';
import { AcceptTermsDocument } from '../mutations/acceptTerms.mutation.generated';
import { GetTermsAcceptanceDocument } from '../queries/getTermsAcceptance.query.generated';
import { GetActiontypeByNameDocument } from '../queries/getActiontypeByName.query.generated';

const { VITE_APP_VERSION } = import.meta.env;

const TERMS_ACTIONTYPE_NAME = 'user.accepted.terms';

// Cache the actiontype ID to avoid repeated queries
let cachedActiontypeId: number | null = null;

const getActiontypeId = async (): Promise<number> => {
  if (cachedActiontypeId !== null) {
    return cachedActiontypeId;
  }

  try {
    // execGraphqlWithFetch returns the array directly for queries
    const result: any = await execGraphqlWithFetch({
      query: GetActiontypeByNameDocument,
      variables: { name: TERMS_ACTIONTYPE_NAME },
    });

    const actiontype = result[0];

    if (!actiontype?.id) {
      throw new Error(`Actiontype '${TERMS_ACTIONTYPE_NAME}' not found in database`);
    }

    cachedActiontypeId = actiontype.id;
    return actiontype.id;
  } catch (err) {
    console.error('Failed to get actiontype ID:', err);
    Sentry.captureException(err, {
      tags: { feature: 'terms-acceptance', action: 'get-actiontype' },
      extra: { actiontypeName: TERMS_ACTIONTYPE_NAME },
    });
    throw err;
  }
};

interface AcceptTermsMetadata {
  user_agreement_version: number;
  user_agreement_published_at: string;
  app_version: string;
  platform: 'web';
  device_id: null;
  user_agent: string;
}

export const acceptTerms = async (
  userId: number,
  userEmail: string,
  publishedVersion: number,
  publishedAt: string
): Promise<void> => {
  try {
    // Get the actiontype ID
    const actiontypeId = await getActiontypeId();

    const metadata: AcceptTermsMetadata = {
      user_agreement_version: publishedVersion,
      user_agreement_published_at: publishedAt,
      app_version: VITE_APP_VERSION || '3.0.0',
      platform: 'web',
      device_id: null,
      user_agent: navigator.userAgent,
    };

    // Get user's IP address (optional - could be handled by backend)
    let ipAddress: string = 'unknown_address';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ipAddress = ipData.ip || 'unknown_address';
    } catch (err) {
      console.warn('Failed to get IP address:', err);
    }

    const result: any = await execGraphqlWithFetch({
      query: AcceptTermsDocument,
      variables: {
        userId,
        actiontypeId,
        ipAddress,
        metadata,
        createdBy: userEmail,
        eventTime: new Date().toISOString(),
      },
    });

    // The result should be the update_users_by_pk object (first selection in mutation)
    // Check if the user update was successful
    if (!result?.accepted_terms) {
      console.error('User accepted_terms field was not updated. Full result:', result);
      const error = new Error(
        'Failed to update user accepted_terms field. This is likely a database permissions issue. ' +
        'The user role may not have permission to update the users table. ' +
        'Please contact your database administrator to grant update permissions on users.accepted_terms for the user role.'
      );

      Sentry.captureException(error, {
        tags: {
          feature: 'terms-acceptance',
          action: 'accept-terms',
          error_type: 'validation_failed'
        },
        extra: {
          userId,
          userEmail,
          publishedVersion,
          publishedAt,
          mutationResult: result,
          actiontypeId
        },
      });

      throw error;
    }
  } catch (err) {
    console.error('Failed to accept terms:', err);

    // Capture the exception with context
    Sentry.captureException(err, {
      tags: {
        feature: 'terms-acceptance',
        action: 'accept-terms',
        error_type: err instanceof Error ? err.name : 'unknown'
      },
      extra: {
        userId,
        userEmail,
        publishedVersion,
        publishedAt,
        errorMessage: err instanceof Error ? err.message : String(err),
        appVersion: VITE_APP_VERSION
      },
      user: {
        id: userId.toString(),
        email: userEmail,
      },
    });

    throw err;
  }
};

export const getTermsAcceptance = async (
  userId: number
): Promise<Usereventlogs | null> => {
  try {
    // Get the actiontype ID
    const actiontypeId = await getActiontypeId();

    // execGraphqlWithFetch returns the array directly for queries
    const result: any = await execGraphqlWithFetch({
      query: GetTermsAcceptanceDocument,
      variables: {
        userId,
        actiontypeId,
      },
    });

    return result[0] || null;
  } catch (err) {
    console.error('Failed to get terms acceptance:', err);
    return null;
  }
};

export const termsService = {
  acceptTerms,
  getTermsAcceptance,
};

export default termsService;
